import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { customerService } from '../../../services/api';
import { parseApiFormErrors } from '../../../services/apiErrors';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';

const SCOPE_VALUES = ['global', 'business', 'team'];
const FORM_FIELDS = ['name', 'scope', 'description'];

// Must match the `violation_error_message` on Role's
// `unique_role_name_per_customer` constraint (core/models/role.py) — swapped
// for a translated message instead of showing the backend's raw English text.
const DUPLICATE_NAME_API_MESSAGE = 'A role with this name already exists.';

export default function RoleForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const roleQuery = useQuery({
    queryKey: ['role', id],
    queryFn: () => customerService.getRole(id).then((res) => res.data),
    enabled: isEditMode,
  });

  const [name, setName] = useState('');
  const [scope, setScope] = useState('business');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [touched, setTouched] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    if (isEditMode && roleQuery.data) {
      setName(roleQuery.data.name);
      setScope(roleQuery.data.scope);
      setDescription(roleQuery.data.description ?? '');
      setIsActive(roleQuery.data.is_active);
      setSelectedPermissions(roleQuery.data.permissions ?? []);
    }
  }, [isEditMode, roleQuery.data]);

  const nameTrimmed = name.trim();
  // Permissions are edited on their own page (/permissions, #601):
  // `selectedPermissions` round-trips through the payload untouched (see
  // mutationFn) so editing a role never wipes out the permissions it has.
  const isValid = nameTrimmed.length > 0;

  const resetFormFields = () => {
    setName('');
    setScope('business');
    setDescription('');
    setIsActive(true);
    setSelectedPermissions([]);
    setTouched(false);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: nameTrimmed,
        scope,
        description,
        is_active: isActive,
        permissions: selectedPermissions,
      };
      return isEditMode ? customerService.updateRole(id, payload) : customerService.createRole(payload);
    },
    // `intent` is whichever submit button was clicked — 'another' only
    // exists in create mode (see the extra button in CardActions).
    onSuccess: (response, intent) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['role', id] });
        navigate(`/roles/${response.data.uuid}`);
        return;
      }
      if (intent === 'another') {
        resetFormFields();
        setFlashMessage(t('roles.saveSuccessCreate'));
        return;
      }
      navigate('/roles', { state: { flashMessage: t('roles.saveSuccessCreate') } });
    },
    onError: (error) => {
      const { fieldErrors: apiFieldErrors, formError: apiFormError } = parseApiFormErrors(error, FORM_FIELDS);
      setFieldErrors(apiFieldErrors);
      const message = apiFormError === DUPLICATE_NAME_API_MESSAGE ? t('roles.validation.nameDuplicate') : apiFormError;
      setFormError(message || t('roles.saveError'));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setFormError('');
    if (!isValid) return;
    const intent = event.nativeEvent.submitter?.value || 'list';
    mutation.mutate(intent);
  };

  const header = (
    <PageHeader
      title={isEditMode ? t('roles.editTitle') : t('roles.createTitle')}
      breadcrumbs={[
        { label: t('roles.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('roles.breadcrumb.list'), to: '/roles' },
        { label: isEditMode ? t('roles.breadcrumb.edit') : t('roles.breadcrumb.new') },
      ]}
    />
  );

  const isLoading = isEditMode && roleQuery.isLoading;

  if (isLoading) {
    return (
      <BoundedPageLayout header={header}>
        <LoadingState message={t('roles.states.loading')} />
      </BoundedPageLayout>
    );
  }

  return (
    <BoundedPageLayout header={header}>
      <Stack component="form" onSubmit={handleSubmit} spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('roles.sections.identification')}
            subheader={t('roles.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('roles.fields.name')}
                  helperText={
                    touched && !nameTrimmed
                      ? t('roles.validation.nameRequired')
                      : fieldErrors.name || t('roles.fields.nameHelper')
                  }
                  error={Boolean((touched && !nameTrimmed) || fieldErrors.name)}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  fullWidth
                />
                <TextField
                  select
                  label={t('roles.fields.scope')}
                  helperText={fieldErrors.scope || t('roles.fields.scopeHelper')}
                  error={Boolean(fieldErrors.scope)}
                  value={scope}
                  onChange={(event) => {
                    setScope(event.target.value);
                    setFieldErrors((prev) => ({ ...prev, scope: undefined }));
                  }}
                  fullWidth
                >
                  {SCOPE_VALUES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`roles.scopes.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label={t('roles.fields.description')}
                helperText={fieldErrors.description || t('roles.fields.descriptionHelper')}
                error={Boolean(fieldErrors.description)}
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setFieldErrors((prev) => ({ ...prev, description: undefined }));
                }}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack>
                  <Typography variant="body1">{t('roles.fields.isActive')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('roles.fields.isActiveHelper')}
                  </Typography>
                </Stack>
                <Switch checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
              </Stack>
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {/* Back to wherever the user came from (list or the role's own
                detail page), not always the list — matches what "Cancel"
                implies: undo getting here. */}
            <Button type="button" color="inherit" onClick={() => navigate(-1)}>
              {t('roles.actions.cancel')}
            </Button>
            {!isEditMode && (
              <Button
                type="submit"
                name="intent"
                value="another"
                variant="outlined"
                disabled={!isValid || mutation.isPending}
              >
                {t('roles.actions.saveAndAddAnother')}
              </Button>
            )}
            <Button
              type="submit"
              name="intent"
              value="list"
              variant="contained"
              disabled={!isValid || mutation.isPending}
            >
              {isEditMode ? t('roles.actions.saveEdit') : t('roles.actions.saveCreate')}
            </Button>
          </CardActions>
        </Card>

      </Stack>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
